import * as React from "react";
import { useRef, useState, useEffect } from "react";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from "@/components/ui/command";
import styles from "@/components/ui/styles/moduleSelector.module.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faBan, faLock } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";

/**
 * Option for a flat selector
 */
export interface BeautifulTabsOption {
    value: string;
    label: React.ReactNode;
    description?: React.ReactNode;
    icon?: React.ReactNode;
    disabled?: boolean;
    enabled?: boolean;
    locked?: boolean;
}

/**
 * Option group for grouped selector
 */
export interface BeautifulTabsOptionGroup {
    group: string;
    type?: string;
    options: BeautifulTabsOption[];
}

export interface BeautifulTabsSelectorProps {
    /**
     * List of options (flat) or groups (grouped)
     */
    options: BeautifulTabsOption[] | BeautifulTabsOptionGroup[];
    /**
     * Currently selected value
     */
    value: string;
    /**
     * Callback when a value is selected
     */
    onChange: (value: string) => void;
    /**
     * If true, treat options as grouped (BeautifulTabsOptionGroup[]), otherwise as flat (BeautifulTabsOption[])
     */
    grouped?: boolean;
    /**
     * Placeholder for the search input
     */
    searchPlaceholder?: string;
    /**
     * Optional: render label for the selector button
     */
    selectorLabel?: React.ReactNode;
    /**
     * Optional: className for root
     */
    className?: string;
}

export const BeautifulTabsSelector: React.FC<BeautifulTabsSelectorProps> = ({
    options,
    value,
    onChange,
    grouped = false,
    searchPlaceholder = "Search...",
    selectorLabel = null,
    className = "",
}) => {
    const [open, setOpen] = useState(false);
    const commandRef = useRef<HTMLDivElement>(null);

    // Auto-detect grouped options by presence of 'options' on first item
    const autoGrouped = Array.isArray(options)
        && (options as any[]).length > 0
        && typeof (options as any[])[0] === "object"
        && ("options" in (options as any[])[0]);
    const isGrouped = grouped || autoGrouped;

    const flatOptions: BeautifulTabsOption[] = isGrouped ? [] : (options as BeautifulTabsOption[]);
    const groupedOptions: BeautifulTabsOptionGroup[] = isGrouped ? (options as BeautifulTabsOptionGroup[]) : [];

    // Find selected label
    const selectedLabel = isGrouped
        ? groupedOptions.flatMap(g => g.options).find(opt => opt.value === value)?.label || ""
        : flatOptions.find(opt => opt.value === value)?.label || "";

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        function handleClick(event: MouseEvent) {
            if (commandRef.current && !commandRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        function handleEscape(event: KeyboardEvent) {
            if (event.key === "Escape") setOpen(false);
        }
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [open]);

    const handleSelect = (val: string) => {
        onChange(val);
        setOpen(false);
    };

    return (
        <div className={`w-full max-w-lg relative ${className}`}>
            <span
                className="inline-flex items-center gap-2 cursor-pointer select-none mb-4"
                onMouseDown={e => {
                    e.stopPropagation();
                    setOpen(o => !o);
                }}
            >
                {/* Identical label style to ModuleSelector */}
                <>
                    <h2 className="text-xl font-semibold text-foreground">{selectorLabel || "Modules"}</h2>
                    <span className="inline-flex items-center gap-2 text-base font-medium text-foreground bg-muted/50 hover:bg-muted/70 border border-border px-4 py-2 rounded-lg transition-colors duration-150">
                        {selectedLabel}
                        <svg
                            className={`w-4 h-4 opacity-70 ${styles.arrowAnim} ${open ? styles.arrowOpen : ""}`}
                            fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </span>
                </>
            </span>
            <div
                ref={commandRef}
                className={`absolute z-50 w-full max-w-lg mt-2 ${styles.moduleBoxAnim} ${open ? styles.moduleBoxAnimOpen : ""}`}
                aria-hidden={!open}
            >
                <Command className="rounded-lg border bg-card shadow-md">
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList>
                        {isGrouped
                            ? groupedOptions.map(group => (
                                <CommandGroup key={group.group} heading={<><span>{group.group}</span> {group.type && <span className="ml-1 text-xs text-muted-foreground">({group.type})</span>}</>}>
                                    {group.options.map(opt => (
                                        <CommandItem
                                            key={opt.value}
                                            value={opt.value}
                                            onSelect={() => !opt.disabled && handleSelect(opt.value)}
                                            className={opt.value === value ? "bg-muted" : ""}
                                            disabled={opt.disabled}
                                        >
                                            {opt.icon && <span className="mr-2">{opt.icon}</span>}
                                            {opt.label}
                                            {opt.enabled !== undefined && (
                                              <span className="ml-auto">
                                                {opt.enabled ? (
                                                  <FontAwesomeIcon icon={faCheck} className="w-4 h-4 text-green-500" />
                                                ) : (
                                                  <FontAwesomeIcon icon={faBan} className="w-4 h-4 text-muted-foreground" />
                                                )}
                                              </span>
                                            )}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            ))
                            : flatOptions.map(opt => (
                                <CommandItem
                                    key={opt.value}
                                    value={opt.value}
                                    onSelect={() => !opt.disabled && handleSelect(opt.value)}
                                    className={opt.value === value ? "bg-muted" : ""}
                                    disabled={opt.disabled}
                                >
                                    {opt.icon && <span className="mr-2">{opt.icon}</span>}
                                    {opt.label}
                                    {opt.enabled !== undefined && (
                                      <span className="ml-auto">
                                        {opt.enabled ? (
                                          <FontAwesomeIcon icon={faCheck} className="w-4 h-4 text-green-500" />
                                        ) : (
                                          <FontAwesomeIcon icon={faBan} className="w-4 h-4 text-muted-foreground" />
                                        )}
                                      </span>
                                    )}
                                </CommandItem>
                            ))}
                    </CommandList>
                </Command>
            </div>
        </div>
    );
};